/**
 * Founder UAT — Training Plan Discussion panel (TalentTrain).
 * Consulting thread between HM Advisor/Admin and Client Admin/HR.
 * The employee never sees this. Markdown-lite + safe clickable links.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, MessageSquare, Pin, Send, ShieldCheck, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/api';

// ---- markdown-lite: **bold**, *italic*, "- " bullets, [text](url), bare URLs.
// Rendered as React elements — no dangerouslySetInnerHTML, no raw HTML.
const URL_RE = /(https?:\/\/[^\s)]+)/g;

function renderInline(text, keyPrefix) {
  const nodes = [];
  let rest = text;
  let k = 0;
  const pattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/;
  while (rest.length) {
    const m = rest.match(pattern);
    if (!m) { nodes.push(...linkify(rest, `${keyPrefix}-t${k}`)); break; }
    if (m.index > 0) nodes.push(...linkify(rest.slice(0, m.index), `${keyPrefix}-t${k}`));
    if (m[1] && m[2]) {
      nodes.push(
        <a key={`${keyPrefix}-a${k}`} href={m[2]} target="_blank" rel="noopener noreferrer"
           className="text-primary underline underline-offset-2 break-all">{m[1]}</a>,
      );
    } else if (m[3]) {
      nodes.push(<strong key={`${keyPrefix}-b${k}`}>{m[3]}</strong>);
    } else if (m[4]) {
      nodes.push(<em key={`${keyPrefix}-i${k}`}>{m[4]}</em>);
    }
    rest = rest.slice(m.index + m[0].length);
    k += 1;
  }
  return nodes;
}

function linkify(text, keyPrefix) {
  const parts = text.split(URL_RE);
  return parts.map((p, i) => (URL_RE.test(p) && p.startsWith('http')
    ? <a key={`${keyPrefix}-u${i}`} href={p} target="_blank" rel="noopener noreferrer"
         className="text-primary underline underline-offset-2 break-all">{p}</a>
    : <React.Fragment key={`${keyPrefix}-f${i}`}>{p}</React.Fragment>));
}

export function MarkdownLite({ text }) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let bullets = [];
  lines.forEach((line, i) => {
    if (/^\s*[-•]\s+/.test(line)) {
      bullets.push(line.replace(/^\s*[-•]\s+/, ''));
      return;
    }
    if (bullets.length) {
      blocks.push(<ul key={`ul-${i}`} className="list-disc pl-5 space-y-0.5">
        {bullets.map((b, j) => <li key={j}>{renderInline(b, `b${i}-${j}`)}</li>)}
      </ul>);
      bullets = [];
    }
    if (line.trim()) blocks.push(<p key={`p-${i}`}>{renderInline(line, `l${i}`)}</p>);
  });
  if (bullets.length) {
    blocks.push(<ul key="ul-end" className="list-disc pl-5 space-y-0.5">
      {bullets.map((b, j) => <li key={j}>{renderInline(b, `bend-${j}`)}</li>)}
    </ul>);
  }
  return <div className="space-y-1.5 text-[15px] leading-relaxed break-words">{blocks}</div>;
}

export default function TrainingDiscussion({ dapi, uid, isHm, canWrite, onUnreadCleared }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [naEditing, setNaEditing] = useState(false);
  const [naText, setNaText] = useState('');
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const d = await dapi.discussionGet(uid);
      setData(d);
      setNaText(d.next_action?.text || '');
      if (d.unread > 0) {
        await dapi.discussionRead(uid);
        onUnreadCleared?.();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { setLoading(true); load(); /* eslint-disable-next-line */ }, [uid]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'nearest' }); }, [data?.messages?.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await dapi.discussionPost(uid, text.trim());
      setText('');
      const d = await dapi.discussionGet(uid);
      setData(d);
      toast.success(t('tt.discussion.sent', { defaultValue: 'Message sent' }));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const saveNextAction = async () => {
    setBusy(true);
    try {
      const r = await dapi.discussionNextAction(uid, naText);
      setData((prev) => ({ ...prev, next_action: r.next_action }));
      setNaEditing(false);
      toast.success(t('tt.discussion.nextActionSaved', { defaultValue: 'Next action updated' }));
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="py-8 text-center text-muted-foreground" data-testid="tt-discussion-loading">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" />{t('common.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }

  const messages = data?.messages || [];
  const na = data?.next_action;

  return (
    <Card className="p-5 shadow-card" data-testid="tt-discussion-panel">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <MessageSquare className="h-5 w-5 text-primary" />
        <h3 className="font-display font-bold text-lg">
          {t('tt.discussion.title', { defaultValue: 'Training plan discussion' })}
        </h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1.5" data-testid="tt-discussion-privacy">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
        {t('tt.discussion.privacy', { defaultValue: 'Private consulting thread between Corporate Talent Hub and your company\u2019s admins — the employee cannot see it.' })}
      </p>

      {/* Pinned next action */}
      {(na || isHm) && (
        <div className="rounded-lg border border-amber-300/60 bg-amber-50/50 px-4 py-3 mb-4" data-testid="tt-discussion-next-action">
          <div className="flex items-center gap-2">
            <Pin className="h-3.5 w-3.5 text-amber-700 shrink-0" />
            <p className="text-[13px] uppercase tracking-wider font-semibold text-amber-800">
              {t('tt.discussion.nextAction', { defaultValue: 'Next action' })}
            </p>
            {isHm && canWrite && !naEditing && (
              <Button size="sm" variant="ghost" className="h-6 px-2 ml-auto"
                      onClick={() => setNaEditing(true)} data-testid="tt-discussion-na-edit">
                <Pencil className="h-3 w-3 mr-1" />{t('common.edit', { defaultValue: 'Edit' })}
              </Button>
            )}
          </div>
          {naEditing ? (
            <div className="mt-2 space-y-2">
              <Textarea value={naText} onChange={(e) => setNaText(e.target.value)} rows={2}
                        className="bg-white text-[15px]" data-testid="tt-discussion-na-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveNextAction} disabled={busy} data-testid="tt-discussion-na-save">
                  {t('common.save', { defaultValue: 'Save' })}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setNaEditing(false); setNaText(na?.text || ''); }}>
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-1.5">
              {na?.text ? (
                <>
                  <MarkdownLite text={na.text} />
                  <p className="text-sm text-muted-foreground mt-1">
                    {na.updated_by_name} · {String(na.updated_at || '').slice(0, 10)}
                  </p>
                </>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  {t('tt.discussion.noNextAction', { defaultValue: 'No next action defined yet.' })}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Thread */}
      <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="tt-discussion-thread">
        {messages.length === 0 && (
          <p className="text-[15px] text-muted-foreground italic py-6 text-center" data-testid="tt-discussion-empty">
            {t('tt.discussion.empty', { defaultValue: 'No messages yet. Use this thread to explain recommendations, suggest providers or coordinate next steps.' })}
          </p>
        )}
        {messages.map((m) => {
          const mine = (m.author_role === 'hm') === isHm;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`tt-discussion-msg-${m.id}`}>
              <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 border ${
                m.author_role === 'hm' ? 'bg-primary/5 border-primary/20' : 'bg-muted/50 border-border'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold">{m.author_name}</span>
                  <Badge variant="outline" className="text-[13px] px-1.5">
                    {m.author_role === 'hm'
                      ? t('tt.discussion.roleHm', { defaultValue: 'Corporate Talent Hub' })
                      : t('tt.discussion.roleClient', { defaultValue: 'Client' })}
                  </Badge>
                  <span className="text-[13px] text-muted-foreground">
                    {String(m.created_at || '').slice(0, 16).replace('T', ' ')}
                  </span>
                </div>
                <MarkdownLite text={m.text} />
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      {canWrite && (
        <div className="mt-4 pt-4 border-t space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            placeholder={t('tt.discussion.placeholder', { defaultValue: 'Write a message… Supports **bold**, *italic*, "- " lists and links.' })}
            className="text-[15px] min-h-32 resize-y"
            data-testid="tt-discussion-input"
          />
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {t('tt.discussion.hint', { defaultValue: 'Formal proposals, contracts and materials continue by email — this thread documents the consulting conversation.' })}
            </p>
            <Button size="sm" onClick={send} disabled={busy || !text.trim()}
                    className="bg-gradient-primary hover:opacity-90" data-testid="tt-discussion-send">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              {t('tt.discussion.send', { defaultValue: 'Send' })}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
